import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Pause, Play, Copy, Trash2, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { api, inr, fmtDate, fmtErr, fileUrl } from "@/lib/api";

const EMPTY = {
  task_code: "", app_name: "", app_logo: "", description: "", play_store_url: "",
  instructionsText: "", estimated_time: "", reward: "", max_participants: "",
  start_date: "", end_date: "", status: "active", allow_multiple: false, allow_resubmit: false, demo_screenshot: "",
};

export default function AdminTasks() {
  const [tasks, setTasks] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingDemo, setUploadingDemo] = useState(false);

  const load = () => api.get("/admin/tasks").then((r) => setTasks(r.data)).catch(() => setTasks([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ ...t, instructionsText: (t.instructions || []).join("\n"), reward: String(t.reward), max_participants: String(t.max_participants) });
    setOpen(true);
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload-logo", fd);
      setForm((prev) => ({ ...prev, app_logo: data.path }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(fmtErr(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const uploadDemo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingDemo(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload-logo", fd);
      setForm((prev) => ({ ...prev, demo_screenshot: data.path }));
      toast.success("Demo screenshot uploaded");
    } catch (err) {
      toast.error(fmtErr(err, "Upload failed"));
    } finally {
      setUploadingDemo(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      reward: parseFloat(form.reward) || 0,
      max_participants: parseInt(form.max_participants) || 0,
      instructions: form.instructionsText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    delete payload.instructionsText;
    delete payload.id;
    delete payload.current_participants;
    delete payload.created_at;
    delete payload.participants_remaining;
    try {
      if (editing) await api.put(`/admin/tasks/${editing.id}`, payload);
      else await api.post("/admin/tasks", payload);
      toast.success(editing ? "Task updated" : "Task created");
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(fmtErr(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (t, status) => {
    try {
      await api.post(`/admin/tasks/${t.id}/status`, { status });
      toast.success(`Task ${status}`);
      await load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const duplicate = async (t) => {
    try {
      const { data } = await api.post(`/admin/tasks/${t.id}/duplicate`);
      toast.success(`Duplicated as ${data.task_code}`);
      await load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete task ${t.task_code} (${t.app_name})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/tasks/${t.id}`);
      toast.success("Task deleted");
      await load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const inCls = "w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div data-testid="admin-tasks-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900">Task Management</h1>
          <p className="mt-1 text-sm text-slate-600">Create, edit, pause, duplicate or delete tasks.</p>
        </div>
        <button onClick={openNew} data-testid="add-task-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm">
          <Plus className="w-4 h-4" /> Add Task
        </button>
      </div>

      {!tasks ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : (
        <div className="mt-6 bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="admin-tasks-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Task</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Reward</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Participants</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Dates</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tasks.map((t) => (
                <tr key={t.id} data-testid={`admin-task-row-${t.task_code}`}>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-3">
                      <img src={fileUrl(t.app_logo) || `https://ui-avatars.com/api/?name=${encodeURIComponent(t.app_name)}&background=2563EB&color=fff&size=96`} alt={t.app_name} className="w-9 h-9 rounded-lg border border-slate-200 object-cover" />
                      <div>
                        <p className="font-semibold text-slate-900">{t.app_name}</p>
                        <p className="text-xs text-slate-500">{t.task_code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 font-semibold text-blue-700">{inr(t.reward)}</td>
                  <td className="px-4 py-3.5 text-slate-700">{t.current_participants}/{t.max_participants}</td>
                  <td className="px-4 py-3.5 text-slate-600 whitespace-nowrap">{fmtDate(t.start_date)} → {fmtDate(t.end_date)}</td>
                  <td className="px-4 py-3.5"><StatusBadge status={t.status} /></td>
                  <td className="px-4 py-3.5">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => openEdit(t)} data-testid={`edit-task-${t.task_code}`} title="Edit" className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"><Pencil className="w-4 h-4" /></button>
                      {t.status === "active" ? (
                        <button onClick={() => setStatus(t, "paused")} data-testid={`pause-task-${t.task_code}`} title="Pause" className="p-2 rounded-lg text-amber-600 hover:bg-amber-50"><Pause className="w-4 h-4" /></button>
                      ) : (
                        <button onClick={() => setStatus(t, "active")} data-testid={`activate-task-${t.task_code}`} title="Activate" className="p-2 rounded-lg text-green-600 hover:bg-green-50"><Play className="w-4 h-4" /></button>
                      )}
                      <button onClick={() => duplicate(t)} data-testid={`duplicate-task-${t.task_code}`} title="Duplicate" className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50"><Copy className="w-4 h-4" /></button>
                      <button onClick={() => remove(t)} data-testid={`delete-task-${t.task_code}`} title="Delete" className="p-2 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {tasks.length === 0 && <p className="p-8 text-center text-sm text-slate-500">No tasks yet. Click "Add Task" to create one.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? `Edit ${editing.task_code}` : "Add New Task"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Task ID *</label>
                <input data-testid="task-form-code" required value={form.task_code} onChange={(e) => setForm({ ...form, task_code: e.target.value })} className={inCls} placeholder="TASK-002" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">App Name *</label>
                <input data-testid="task-form-app-name" required value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} className={inCls} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">App Logo</label>
              <div className="flex items-center gap-3">
                {form.app_logo ? <img src={fileUrl(form.app_logo)} alt="logo" data-testid="task-form-logo-preview" className="w-11 h-11 rounded-xl border border-slate-200 object-cover" /> : null}
                <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 cursor-pointer">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
                  <input data-testid="task-form-logo-upload" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadLogo} />
                </label>
                <input data-testid="task-form-logo-url" value={form.app_logo} onChange={(e) => setForm({ ...form, app_logo: e.target.value })} className={inCls} placeholder="or paste logo URL" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Demo Screenshot <span className="text-slate-400 font-normal">(optional — shows users how the proof screenshot should look)</span></label>
              <div className="flex items-center gap-3">
                {form.demo_screenshot ? <img src={fileUrl(form.demo_screenshot)} alt="demo" data-testid="task-form-demo-preview" className="h-16 rounded-xl border border-slate-200 object-cover" /> : null}
                <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 cursor-pointer">
                  {uploadingDemo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload Demo SS
                  <input data-testid="task-form-demo-upload" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadDemo} />
                </label>
                {form.demo_screenshot ? (
                  <button type="button" data-testid="task-form-demo-remove" onClick={() => setForm({ ...form, demo_screenshot: "" })} className="text-xs font-semibold text-red-600 hover:text-red-700">Remove</button>
                ) : null}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">App Description *</label>
              <textarea data-testid="task-form-description" required rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Google Play Store URL *</label>
              <input data-testid="task-form-playstore-url" required type="url" value={form.play_store_url} onChange={(e) => setForm({ ...form, play_store_url: e.target.value })} className={inCls} placeholder="https://play.google.com/store/apps/details?id=..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Task Instructions (one per line) *</label>
              <textarea data-testid="task-form-instructions" required rows={5} value={form.instructionsText} onChange={(e) => setForm({ ...form, instructionsText: e.target.value })} className={inCls} placeholder={"Open the Google Play Store link.\nInstall the app.\n..."} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Est. Time *</label>
                <input data-testid="task-form-time" required value={form.estimated_time} onChange={(e) => setForm({ ...form, estimated_time: e.target.value })} className={inCls} placeholder="10 minutes" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reward (₹) *</label>
                <input data-testid="task-form-reward" required type="number" min="1" value={form.reward} onChange={(e) => setForm({ ...form, reward: e.target.value })} className={inCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Max Participants *</label>
                <input data-testid="task-form-max-participants" required type="number" min="1" value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: e.target.value })} className={inCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select data-testid="task-form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inCls}>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                <input data-testid="task-form-start-date" type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
                <input data-testid="task-form-end-date" type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inCls} />
              </div>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input data-testid="task-form-allow-multiple" type="checkbox" checked={!!form.allow_multiple} onChange={(e) => setForm({ ...form, allow_multiple: e.target.checked })} className="w-4 h-4 rounded border-slate-300 text-blue-600" />
                Allow multiple submissions per user
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input data-testid="task-form-allow-resubmit" type="checkbox" checked={!!form.allow_resubmit} onChange={(e) => setForm({ ...form, allow_resubmit: e.target.checked })} className="w-4 h-4 rounded border-slate-300 text-blue-600" />
                Allow resubmission after rejection
              </label>
            </div>
            <button type="submit" data-testid="task-form-save-btn" disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Save Changes" : "Create Task"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
